// Considered: TagAtom — rejected as the whole component because the wireframe rail entry
// (docs/design/context-selector/advanced-filters.jsx:706-723) is a full-width <button>
// composing a mono token + description + grip handle, not a bare tag; this molecule
// composes TagAtom for the token only, adding the draggable/click-to-insert row shell.
// ChipAtom rejected: PrimeReact-wrapped, no tone/pressed/mono API. See spec
// context-selector-editor.md Phase 7 (SC7) Reuse Audit.
import React, { useCallback } from 'react';
import TagAtom, { type TagTone } from '../../atoms/Tag/TagAtom';
import { FILTER_DND_MIME } from '../../../utils/filterDnd';
import './FilterVariableRowMolecule.scss';

export interface FilterVariableRowMoleculeProps {
  /** The literal `@`-token this row inserts (e.g. `@me`). Never resolved client-side. */
  token: string;
  /** Human-readable description shown beside the token (e.g. "Current user"). */
  label: string;
  /** Tone for the token's `TagAtom` — differentiates variables without bare hex/rgba. */
  tone?: TagTone;
  /** Called on click (and Enter/Space, since this renders a native `<button>`) with the literal token. */
  onInsert: (token: string) => void;
  /**
   * True when this variable can't apply to the currently focused row's field
   * type (e.g. `@today` while a person field is focused) — renders disabled
   * with `reason` shown in place of the description label (adjacent text,
   * not title-only — standards/frontend/ux-craft.md I2) so the author sees
   * why without hovering, rather than a token that silently does nothing
   * (`todayOfferedOnlyForDates` et al.). Still set as `title` too, for the
   * mouse-hover affordance.
   */
  disabled?: boolean;
  /** Why this variable is disabled — required whenever `disabled` is true. */
  reason?: string;
  'data-testid'?: string;
  /** Parity datum id for the row itself (the button). */
  'data-datum'?: string;
  /** Parity datum id for the token value (`{v.v}` in the prototype). */
  'data-datum-value'?: string;
  /** Parity container the row stands in (the rail's `each(variables)` collection). */
  'data-container'?: string;
}

const FilterVariableRowMolecule: React.FC<FilterVariableRowMoleculeProps> = ({
  token,
  label,
  tone = 'neutral',
  onInsert,
  disabled = false,
  reason,
  'data-testid': testId,
  'data-datum': dataDatum,
  'data-datum-value': dataDatumValue,
  'data-container': dataContainer,
}) => {
  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLButtonElement>) => {
      // Typed payload (custom MIME) so the filter editor's drop targets can add a
      // row / set a value; text/plain stays the raw token for plain inputs.
      e.dataTransfer.setData(
        FILTER_DND_MIME,
        JSON.stringify({ kind: 'rail-variable', token })
      );
      e.dataTransfer.setData('text/plain', token);
      e.dataTransfer.effectAllowed = 'copy';
    },
    [token]
  );

  const handleClick = useCallback(() => onInsert(token), [onInsert, token]);

  return (
    <button
      type="button"
      className="filter-variable-row-molecule"
      draggable={!disabled}
      onDragStart={disabled ? undefined : handleDragStart}
      onClick={disabled ? undefined : handleClick}
      disabled={disabled}
      title={disabled ? reason : undefined}
      data-testid={testId ?? `variable-insert-${token}`}
      data-container={dataContainer}
      data-datum={dataDatum}
    >
      <TagAtom
        label={token}
        tone={tone}
        size="small"
        className="filter-variable-row-molecule__token"
        data-datum={dataDatumValue}
        data-kind="value"
      />
      {/* `__label` carries flex:1, so it doubles as the wireframe's spacer and
          pushes the grip to the trailing edge (advanced-filters.jsx:717-723).
          Disabled swaps the description for the reason, so the "why" is
          visible without a hover (ux-craft.md I2) rather than title-only. */}
      <span
        className={`filter-variable-row-molecule__label${
          disabled && reason ? ' filter-variable-row-molecule__label--reason' : ''
        }`}
      >
        {disabled && reason ? reason : label}
      </span>
      <i className="pi pi-bars filter-variable-row-molecule__grip" aria-hidden="true" />
    </button>
  );
};

export default FilterVariableRowMolecule;
